import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import CabDefinitionForm from './CabDefinitionForm';
import {
  createDefaultCabDefinitionFormData,
  formDataToSavePayload,
  saveCabDefinition,
} from '../../../services/cabDefinitionsService';
import type { CabDefinitionFormData } from '../../../types/cabDefinition';
import {
  hasValidationErrors,
  validateCabDefinitionForm,
  type CabDefinitionFormErrors,
} from '../../../utils/cabDefinitionValidation';

const CabDefinitionCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CabDefinitionFormData>(createDefaultCabDefinitionFormData);
  const [errors, setErrors] = useState<CabDefinitionFormErrors>({});

  const saveMutation = useMutation({
    mutationFn: saveCabDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cab-definitions'] });
    },
  });

  const handleChange = (updates: Partial<CabDefinitionFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    const clearedKeys = Object.keys(updates) as Array<keyof CabDefinitionFormData>;
    setErrors((prev) => {
      const next = { ...prev };
      clearedKeys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const handleSubmit = async (action: 'save' | 'saveContinue') => {
    const validationErrors = validateCabDefinitionForm(formData);
    if (hasValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors before saving.');
      return;
    }

    try {
      const payload = formDataToSavePayload(formData);
      const result = await saveMutation.mutateAsync(payload);
      toast.success('CAB Definition saved successfully.');

      if (action === 'saveContinue') {
        setFormData(createDefaultCabDefinitionFormData());
        setErrors({});
        return;
      }

      navigate(`/admin/cab-meeting-management/cab-definitions/${result.record.id}`);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || 'Failed to save CAB Definition. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/admin/cab-meeting-management/cab-definitions');
  };

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-auto">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              CAB Meeting Management
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              All CAB Definitions
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium">New</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6 flex-1">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">CAB Definition</h1>
          <p className="text-sm text-gray-600 mt-1">New Record</p>
        </div>

        <CabDefinitionForm
          formData={formData}
          errors={errors}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSaving={saveMutation.isPending}
        />
      </div>
    </div>
  );
};

export default CabDefinitionCreate;
